import type { MenuTab, SideMenuProps } from '@main/core-ui';
import { Icon } from '@main/core-ui';
import { getMessageAsString } from '@main/internationalization';
import { SideMenu } from '@main/privacy-center/src/components/SideMenu';
import { indexTree } from '@main/utils';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { sidebarMobileMessages } from './messages';
import {
  HideMenuWrapper,
  LabelWrapper,
  MainNavWrapper,
  SidebarMobileWrapper,
  SideMenuCollapsedWrapper,
  SideMenuWrapper,
  SubNavWrapper,
} from './wrappers';

/**
 * Page SidebarMobile for mobile
 */
export const SidebarMobile = ({
  tabs,
  selected,
  ...rest
}: SideMenuProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  // Indicates if menu is open
  const [isOpen, setIsOpen] = useState(false);

  // The target element to freeze against
  const targetElement = useRef<HTMLDivElement>(null);

  const menuClosedRef = useRef<HTMLDivElement>(null);
  const menuOpenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus the open menu button
    if (isOpen && menuOpenRef.current) {
      menuOpenRef.current.querySelector('button')?.focus();
    }
  }, [isOpen]);

  // Calculate parent menu
  const [selectedMenu, parentMenu] = useMemo((): [
    MenuTab | undefined,
    MenuTab | undefined,
  ] => {
    // Determine default selectedMenu
    if (selected) {
      // We need to index the tree
      const { parentLookup, lookupTreeTab } = indexTree(tabs, 'visualId');

      // Set the selected menu
      const newSelectedMenu = lookupTreeTab[selected];

      // Determine the parent
      const parents = parentLookup[selected] || [];
      const newParentMenu = lookupTreeTab[parents[parents.length - 1]];
      return [newSelectedMenu, newParentMenu];
    }
    return [undefined, undefined];
  }, [selected, tabs]);

  // Callback to toggle menu
  const toggleMenu = (): void => {
    // Focus the close menu button
    if (isOpen && menuClosedRef.current) {
      menuClosedRef.current.querySelector('button')?.focus();
    }

    // Toggle open state
    setIsOpen(!isOpen);

    // Allow or disallow scroll
    if (targetElement.current) {
      if (isOpen) {
        clearAllBodyScrollLocks();
      } else {
        disableBodyScroll(targetElement.current);
      }
    }
  };

  return (
    <SidebarMobileWrapper>
      <div ref={menuClosedRef}>
        <SideMenuCollapsedWrapper
          id="sidebar-mobile-toggle"
          onClick={toggleMenu}
          aria-label={formatMessage(sidebarMobileMessages.showMenu)}
        >
          <LabelWrapper>
            <MainNavWrapper>
              {parentMenu?.title &&
                getMessageAsString(
                  parentMenu.title as any,
                  formatMessage as any,
                )}
            </MainNavWrapper>
            {selectedMenu?.title && (
              <SubNavWrapper>
                {getMessageAsString(
                  selectedMenu.title as any,
                  formatMessage as any,
                )}
              </SubNavWrapper>
            )}
          </LabelWrapper>
          <Icon
            type="caret-down"
            size={24}
            color={theme.colors.textOnSidebar}
          />
        </SideMenuCollapsedWrapper>
      </div>
      <SideMenuWrapper hidden={!isOpen} ref={targetElement}>
        <div ref={menuOpenRef}>
          <SideMenuCollapsedWrapper
            onClick={toggleMenu}
            aria-label={formatMessage(sidebarMobileMessages.hideMenu)}
          >
            <HideMenuWrapper>
              {formatMessage(sidebarMobileMessages.hideMenu)}
            </HideMenuWrapper>
            <Icon
              type="caret-up"
              size={24}
              color={theme.colors.textOnSidebar}
            />
          </SideMenuCollapsedWrapper>
        </div>
        <SideMenu
          {...rest}
          tabs={tabs}
          selected={selected}
          onSelectChange={(options) => {
            // Call onSelectChange
            if (rest?.onSelectChange) {
              rest.onSelectChange(options);
            }

            // Update state
            setIsOpen(false);
            clearAllBodyScrollLocks();
          }}
        />
      </SideMenuWrapper>
    </SidebarMobileWrapper>
  );
};
